import React from 'react';
import { useTheme } from '../theme/ThemeContext';

const iconProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    width: 22,
    height: 22,
    'aria-hidden': true,
};

const CpuIcon = () => (
    <svg {...iconProps}>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="1" x2="9" y2="4" />
        <line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" />
        <line x1="15" y1="20" x2="15" y2="23" />
        <line x1="20" y1="9" x2="23" y2="9" />
        <line x1="20" y1="14" x2="23" y2="14" />
        <line x1="1" y1="9" x2="4" y2="9" />
        <line x1="1" y1="14" x2="4" y2="14" />
    </svg>
);

const ZapIcon = () => (
    <svg {...iconProps}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const ToolIcon = () => (
    <svg {...iconProps}>
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
);

const ShieldIcon = () => (
    <svg {...iconProps}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const DatabaseIcon = () => (
    <svg {...iconProps}>
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const tagColorFor = (i) => ['cyan', 'purple', 'pink'][i % 3];

const skillCategories = [
    {
        icon: CpuIcon,
        category: 'AI & Machine Learning',
        name: 'Model Development',
        skills: ['PyTorch', 'TensorFlow', 'CNN Architectures', 'Transfer Learning', 'Computer Vision', 'OpenCV', 'LLM Integration (Ollama)'],
    },
    {
        icon: ZapIcon,
        category: 'Architecture & Infrastructure',
        name: 'Distributed Design',
        skills: ['Microservices', 'Kubernetes', 'Docker', 'RabbitMQ', 'REST', 'GraphQL', 'Saga & CQRS', 'Eventual Consistency', 'OAuth2 / JWT'],
    },
    {
        icon: ToolIcon,
        category: 'Languages & Frameworks',
        name: 'Production Stack',
        skills: ['Java', 'Spring Boot', 'Python', 'FastAPI', 'Node.js', 'Express', 'React', 'JavaScript / TypeScript'],
    },
    {
        icon: DatabaseIcon,
        category: 'Data & Databases',
        name: 'Storage & Modeling',
        skills: ['PostgreSQL', 'MySQL', 'MongoDB', 'Neo4j', 'Read Replicas & Sharding', 'Query Optimization', 'ETL Pipelines'],
    },
    {
        icon: ShieldIcon,
        category: 'Quality & Testing',
        name: 'Verification & Delivery',
        skills: ['Unit & Integration Tests', 'Playwright (E2E)', 'Postman', 'Grafana k6 (Stress)', 'UAT', 'GitHub Actions / CI/CD'],
    },
];

function SkillsDashboard() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <section className="section" id="skills">
            <div className="container">
                <div className="section-header reveal">
                    <span className="section-label">{isDark ? '# 03. CAPABILITIES' : '03 — Skills'}</span>
                    <h2 className="section-header__title">{isDark ? 'System Metrics' : 'Skills'}</h2>
                    <p className="section-header__subtitle">
                        Grouped by domain — every entry is backed by the projects and
                        experience below.
                    </p>
                </div>

                <div className="skills-dashboard">
                    {skillCategories.map((cat, catIdx) => (
                        <div
                            key={cat.category}
                            className={`skill-tile reveal reveal-delay-${catIdx + 1}`}
                        >
                            <span className="skill-tile__icon"><cat.icon /></span>
                            <p className="skill-tile__category">{cat.category}</p>
                            <p className="skill-tile__name">{cat.name}</p>
                            <div className="skill-tile__tags">
                                {cat.skills.map((skill, i) => (
                                    <span key={skill} className={`tech-tag tech-tag--${tagColorFor(i)}`}>
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default SkillsDashboard;